import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

export const FORGOT_PIN_HOLD_MS = 10_000;
export const PARENT_RESET_CONFIRMATION = "RESET PARENT MODE";

interface ForgotPinRecoveryProps {
  onReset: (confirmation: string) => void;
}

export function ForgotPinRecovery({ onReset }: ForgotPinRecoveryProps) {
  const [open, setOpen] = useState(false);
  const [holding, setHolding] = useState(false);
  const [ready, setReady] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const timerRef = useRef<number | null>(null);

  const stopHold = (): void => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = null;
    setHolding(false);
  };

  const startHold = (): void => {
    if (ready || timerRef.current !== null) return;
    setError("");
    setHolding(true);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setHolding(false);
      setReady(true);
    }, FORGOT_PIN_HOLD_MS);
  };

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>): void => {
    if (event.button !== 0) return;
    event.preventDefault();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture is a usability aid; the hold still remains bounded.
    }
    startHold();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if ((event.key === " " || event.key === "Enter") && !event.repeat) {
      event.preventDefault();
      startHold();
    }
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      stopHold();
    }
  };

  if (!open) {
    return (
      <button className="parent-forgot-action" onClick={() => setOpen(true)} type="button">
        Forgot PIN?
      </button>
    );
  }

  const reset = (): void => {
    setError("");
    try {
      onReset(confirmation);
    } catch {
      setError(`Type ${PARENT_RESET_CONFIRMATION} exactly to reset Parent Mode.`);
    }
  };

  return (
    <section className="parent-forgot-recovery" aria-labelledby="forgot-pin-title">
      <h2 id="forgot-pin-title">Reset forgotten PIN</h2>
      <p>
        Recovery disables the current Child Controls and returns Parent Mode to first-time setup. It
        removes only the local Parent PIN, controls and cooldown. Your drawings, comics and artwork
        stay unchanged.
      </p>
      {!ready ? (
        <>
          <p id="parent-reset-hold-help">Press and continuously hold for 10 seconds to continue.</p>
          <button
            aria-describedby="parent-reset-hold-help"
            className={`button button--secondary parent-reset-hold${holding ? " parent-reset-hold--active" : ""}`}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onPointerCancel={stopHold}
            onPointerDown={handlePointerDown}
            onPointerLeave={stopHold}
            onPointerUp={stopHold}
            type="button"
          >
            {holding ? "Keep holding…" : "Hold 10 seconds to continue"}
          </button>
          <p aria-live="polite" className="parent-hold-status">
            {holding ? "Recovery hold started. Keep holding continuously." : ""}
          </p>
        </>
      ) : (
        <div className="parent-reset-confirmation">
          <p role="status">Hold complete. One final confirmation is required.</p>
          <label className="form-field" htmlFor="forgot-pin-confirmation">
            <span>Type {PARENT_RESET_CONFIRMATION} to confirm</span>
            <input
              autoComplete="off"
              id="forgot-pin-confirmation"
              onChange={(event) => setConfirmation(event.target.value)}
              type="text"
              value={confirmation}
            />
          </label>
          {error ? <p role="alert">{error}</p> : null}
          <button className="button button--danger" onClick={reset} type="button">
            Reset Parent Mode only
          </button>
        </div>
      )}
      <button
        className="button button--secondary button--small"
        onClick={() => {
          stopHold();
          setOpen(false);
          setReady(false);
          setConfirmation("");
          setError("");
        }}
        type="button"
      >
        Cancel recovery
      </button>
    </section>
  );
}
